// src/hooks/useTelegramPageGuards.ts
import { useEffect } from "react";

type Options = {
  shouldConfirmClose: boolean;       // when true, enable Telegram's closing confirmation (X / swipe down)
  backConfirmMessage?: string;       // message for our custom "Back" confirm
  onConfirmBack?: () => void;        // action to run after user confirms going back
};

export function useTelegramPageGuards({
  shouldConfirmClose,
  backConfirmMessage,
  onConfirmBack,
}: Options) {
  useEffect(() => {
    const wa = (window as any)?.Telegram?.WebApp;
    if (!wa) return;

    // ----- Back button handling -----
    const onBack = () => {
      // If no confirmation needed, just run the callback
      if (!shouldConfirmClose) {
        onConfirmBack?.();
        return;
      }

      // Otherwise, show a Telegram popup with our custom text
      wa.showPopup(
        {
          title: "Quit this game?",
          message: backConfirmMessage ?? "Your current round will be closed.",
          buttons: [
            { id: "ok", type: "destructive", text: "Leave" },
            { type: "cancel" },
          ],
        },
        (buttonId?: string) => {
          if (buttonId === "ok") onConfirmBack?.();
        }
      );
    };

    // Show BackButton and bind handler
    wa.BackButton.show();
    wa.onEvent("backButtonClicked", onBack);

    // ----- Close (X / swipe down) guarding -----
    // When enabled, Telegram shows its native "close confirmation".
    if (shouldConfirmClose) {
      wa.enableClosingConfirmation();
    } else {
      wa.disableClosingConfirmation();
    }

    // Optional: pause/cleanup when app becomes inactive
    const onDeactivated = () => {
      // you could pause timers here if needed
    };
    wa.onEvent?.("deactivated", onDeactivated);

    return () => {
      wa.offEvent?.("backButtonClicked", onBack);
      wa.offEvent?.("deactivated", onDeactivated);
      wa.BackButton.hide();
      // always ensure confirmation is off when leaving this screen
      wa.disableClosingConfirmation?.();
    };
  }, [shouldConfirmClose, backConfirmMessage, onConfirmBack]);
}
